import React from "react";
import { Card, Typography, Tooltip } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from "@ant-design/icons";
import { MetricSummary } from "interfaces/marketplace-dashboard";
import { Comparison } from "interfaces/marketplace-dashboard";

const { Text, Title } = Typography;

interface MetricCardProps {
  title: string;
  description: string;
  summary: MetricSummary | null;
  comparison: Comparison;
  formatValue?: (v: number | null) => string;
}

const defaultFormat = (v: number | null): string => {
  if (v === null || v === undefined) return "—";
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  if (v % 1 !== 0) return v.toFixed(4);
  return v.toLocaleString();
};

const comparisonLabel: Record<Comparison, string> = {
  wow: "vs prev week",
  mom: "vs prev month",
  qoq: "vs prev quarter",
  yoy: "vs prev year",
  none: "",
};

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  description,
  summary,
  comparison,
  formatValue = defaultFormat,
}) => {
  const value = summary?.value ?? null;
  const baselineValue = summary?.baselineValue ?? null;
  const percentChange = summary?.percentChange ?? null;
  const absoluteChange = summary?.absoluteChange ?? null;

  const hasComparison = comparison !== "none" && percentChange !== null;

  const isPositive = (percentChange ?? 0) > 0;
  const isNeutral = (percentChange ?? 0) === 0;

  const changeColor = isNeutral ? "#888" : isPositive ? "#52c41a" : "#ff4d4f";
  const ChangeIcon = isNeutral ? MinusOutlined : isPositive ? ArrowUpOutlined : ArrowDownOutlined;

  return (
    <Card className="metric-card" bordered={false}>
      <div className="metric-card__header">
        <Tooltip title={description}>
          <Text className="metric-card__title">{title}</Text>
        </Tooltip>
      </div>

      <Title level={2} className="metric-card__value">
        {formatValue(value)}
      </Title>

      {hasComparison && (
        <div className="metric-card__change">
          <span style={{ color: changeColor, fontWeight: 600, fontSize: 14 }}>
            <ChangeIcon style={{ marginRight: 4 }} />
            {Math.abs(percentChange!).toFixed(1)}%
          </span>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
            {comparisonLabel[comparison]}
          </Text>
        </div>
      )}

      {hasComparison && baselineValue !== null && (
        <div className="metric-card__baseline">
          <Text type="secondary" style={{ fontSize: 12 }}>
            Baseline: {formatValue(baselineValue)}
            {absoluteChange !== null && (
              <span style={{ marginLeft: 6, color: changeColor }}>
                ({absoluteChange >= 0 ? "+" : ""}
                {formatValue(absoluteChange)})
              </span>
            )}
          </Text>
        </div>
      )}

      {comparison !== "none" && percentChange === null && baselineValue !== null && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No comparison data
        </Text>
      )}
    </Card>
  );
};

export default MetricCard;
